import {Component, OnDestroy, OnInit} from '@angular/core';
import { AuthService } from '../../../core/services/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import {
  PagedContactTypeResponse,
  SearchContactTypeModel,
} from '../../../core/models/contact-type.model';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MunicipalityService } from '../../../core/services/municipality/municipality/municipality.service';
import {ReactiveFormsModule, FormBuilder, FormGroup, FormsModule} from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import {PendingAlertsCardComponent} from '../../../shared/components/pending-alerts/pending-alerts-card.component';
import {
  PendingPatientsAlertService
} from '../../../core/services/pending-patients-alert/location/pending-patients-alert.service';
import {ResponseRequestModel} from '../../../core/models/response.model';
import {HealthUnitService} from '../../../core/services/health-unit/location/health-unit.service';
import {MatButton} from '@angular/material/button';
import {SnackbarCustomService} from '../../../shared/components/snackbar/snackbar-custom.service';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ScrollingModule} from '@angular/cdk/scrolling';

@Component({
  selector: 'pending-patient-alerts',
  imports: [
    MatPaginatorModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatFormField,
    MatLabel,
    PendingAlertsCardComponent,
    MatButton,
    FormsModule,
    MatIcon,
    MatTooltip,
    MatProgressSpinner,
    ScrollingModule,
  ],
  templateUrl: './pending-patient-alerts.component.html',
  styleUrl: './pending-patient-alerts.component.scss',
  providers: [PendingPatientsAlertService, AuthService],
  standalone: true,
})
export class PendingPatientAlertsComponent implements OnInit, OnDestroy {
  constructor(
    private pendingPatientsAlertService: PendingPatientsAlertService,
    private authService: AuthService,
    private healthUnitService: HealthUnitService,
    private dialog: MatDialog,
    private municipalityService: MunicipalityService,
    private formBuilder: FormBuilder,
    private snackbar: SnackbarCustomService
  ) {}

  page: number = 1;
  totalItems: number = 0;
  rows: number = 10;
  numberRows: number = 0;
  itemsList: {Leve: any[], Moderado: any[], Critico: any[]} = {Leve: [], Moderado: [], Critico: []};
  loading: boolean = false;
  searchForm: any = {};
  inputSearch: string = '';
  municipioId: number | null = null;
  unidadeS_id: number | null = null;
  status_id: number | null = null;

  form!: FormGroup;
  municipiosList: any[] = [];
  unidadeSaudeList: any[] = [];
  screen: string = 'dashboard-gestao';
  showSelectMunicipio: boolean = true;
  showSelectUnidade: boolean = true;

  // Timer
  timer: number = 10000;
  timerStoped: boolean = false;
  intervalId: any = null;

  // Controle de novos alertas críticos
  previousCriticoCount = 0;
  audioContext?: AudioContext;

  ngOnInit() {
    this.createForm();
    this.getMunicipio();
    this.form.get('status')?.setValue(1);
    this.status_id = 1;

    // Configurar município do usuário
    if(this.authService.getMunicipio()){
      this.form.get('municipio')?.setValue(Number(this.authService.getMunicipio()));
      this.municipioId = Number(this.authService.getMunicipio());
      this.getUnidadesDeSaude(this.municipioId);
      this.form.get('unidadeSaude')?.enable();
    }

    // Configurar visibilidade dos selects baseado no perfil
    if(this.authService.getProfile() === "5") {
      this.showSelectMunicipio = false;
    }
    if(this.authService.getProfile() === "3" || this.authService.getProfile() === "4") {
      this.unidadeS_id = Number(this.authService.getUnidade() || null);
      this.showSelectUnidade = false;
      this.showSelectMunicipio = false;
    }

    // Inicializar contexto de áudio
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      this.audioContext = new AudioContext();
    }

    // Solicitar permissão para notificações
    this.requestNotificationPermission();

    this.getListItems();
    this.timerGetItems();
  }

  ngOnDestroy() {
    this.stopTimer();
  }

  /**
   * Solicita permissão para notificações do navegador
   */
  requestNotificationPermission(): void {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  /**
   * Retorna label legível do timer
   */
  getTimerLabel(timerValue: number): string {
    const labels: {[key: number]: string} = {
      10000: '10 segundos',
      120000: '2 minutos',
      300000: '5 minutos'
    };
    return labels[timerValue] || `${timerValue / 1000} segundos`;
  }

  /**
   * Retorna nome do município pelo ID
   */
  getMunicipioNome(id: number): string {
    const municipio = this.municipiosList.find(m => m.id === id);
    return municipio?.nome || '';
  }

  /**
   * TrackBy function para otimizar renderização
   */
  trackByAlertId(index: number, item: any): number {
    return item.alerta_id;
  }

  /**
   * Lida com resposta de ações nos cards
   */
  handleAlertResponse(tipo: 'Leve' | 'Moderado' | 'Critico'): void {
    this.itemsList[tipo] = [];
    this.getListItems();
  }

  /**
   * Altera intervalo do timer
   */
  onTimerChange(value: number): void {
    this.timer = value;
    this.announceToScreenReader(`Intervalo de atualização alterado para ${this.getTimerLabel(value)}`);
    this.timerGetItems();
  }

  /**
   * Inicia timer de atualização automática
   */
  timerGetItems(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.timerStoped = false;
    this.snackbar.success('Atualização automática iniciada!');
    this.announceToScreenReader('Atualização automática de alertas iniciada');

    this.intervalId = setInterval(() => {
      this.getListItems();
    }, this.timer);
  }

  /**
   * Para timer de atualização automática
   */
  stopTimer(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.snackbar.success('Atualização automática pausada!');
      this.announceToScreenReader('Atualização automática de alertas pausada');
      this.timerStoped = true;
      this.intervalId = null;
    }
  }

  /**
   * Cria formulário de filtros
   */
  createForm(): void {
    this.form = this.formBuilder.group({
      municipio: [null],
      unidadeSaude: [{value: null, disabled: true}],
      status: [null],
    });
  }

  /**
   * Limpa todos os filtros aplicados
   */
  clearFilters(): void {
    if(this.authService.getProfile() === "1") {
      this.form.reset({
        municipio: null,
        unidadeSaude: null,
        status: null,
      });
      this.status_id = null;
      this.unidadeS_id = null;
      this.municipioId = null;
      this.form.get('unidadeSaude')?.disable();
    } else if(this.authService.getProfile() === "5") {
      this.form.reset({
        unidadeSaude: null,
        status: null,
      });
      this.status_id = null;
      this.unidadeS_id = null;
    } else if(this.authService.getProfile() === "3" || this.authService.getProfile() === "4") {
      this.form.reset({
        status: null,
      });
      this.status_id = null;
    }

    this.announceToScreenReader('Filtros limpos');
    this.getListItems();
  }

  /**
   * Carrega lista de municípios
   */
  getMunicipio(): void {
    this.municipalityService.listActiveMunicipality().subscribe({
      next: (response: any) => {
        if (response.data) {
          this.municipiosList = response.data;
        }
      },
      error: (err: any) => {
        console.error(err);
      },
    });
  }

  /**
   * Carrega unidades de saúde do município
   */
  getUnidadesDeSaude(municipioId: number): void {
    if (!municipioId) {
      this.unidadeSaudeList = [];
      return;
    }
    this.healthUnitService.listActiveLocationByMunicip(municipioId).subscribe({
      next: (response: ResponseRequestModel) => {
        this.unidadeSaudeList = response.data;
      },
      error: (err: ResponseRequestModel) => {
        console.error(err);
      }
    });
  }

  /**
   * Manipula mudança de município
   */
  onMunicipioChange(): void {
    if(this.municipioId != this.form.get('municipio')?.value){
      this.itemsList.Leve = [];
      this.itemsList.Moderado = [];
      this.itemsList.Critico = [];
      this.totalItems = 0;
    }
    const municipioId = this.form.get('municipio')?.value;
    this.form.get('unidadeSaude')?.reset();

    if (municipioId) {
      this.form.get('unidadeSaude')?.enable();
      this.getUnidadesDeSaude(municipioId);
    } else {
      this.form.get('unidadeSaude')?.disable();
    }

    this.onChange();
  }

  /**
   * Verifica permissão do usuário
   */
  checkPermission(permission: string): boolean {
    return this.authService.checkPermission(permission, this.screen);
  }

  /**
   * Manipula mudança nos filtros
   */
  onChange(): void {
    this.municipioId = this.form.get('municipio')?.value;

    if(this.status_id != this.form.get('status')?.value){
      this.itemsList.Leve = [];
      this.itemsList.Moderado = [];
      this.itemsList.Critico = [];
      this.totalItems = 0;
    }
    if(this.unidadeS_id != this.form.get('unidadeSaude')?.value){
      this.itemsList.Leve = [];
      this.itemsList.Moderado = [];
      this.itemsList.Critico = [];
      this.totalItems = 0;
    }
    this.status_id = this.form.get('status')?.value;
    this.unidadeS_id = this.form.get('unidadeSaude')?.value;
    this.getListItems();
  }

  /**
   * Trata resposta da API de alertas
   */
  treatResponse(response: any): void {
    if (response.alertas) {
      if(response.total){
        this.totalItems = response.total;
      }
      const alertas = response.alertas;

      // Processar alertas por tipo
      this.processAlertsByType(alertas.Leve, 'Leve');
      this.processAlertsByType(alertas.Moderado, 'Moderado');
      this.processAlertsByType(alertas.Critico, 'Critico');

      // Detectar novos alertas críticos
      const newCriticoCount = this.itemsList.Critico.length;
      if (newCriticoCount > this.previousCriticoCount) {
        const newAlerts = newCriticoCount - this.previousCriticoCount;
        this.notifyNewCriticalAlert(newAlerts);
      }
      this.previousCriticoCount = newCriticoCount;
    }
  }

  /**
   * Processa alertas de um tipo específico
   */
  private processAlertsByType(alertas: any[], tipo: 'Leve' | 'Moderado' | 'Critico'): void {
    if (!alertas) return;

    for (const alerta of alertas) {
      const existe = this.itemsList[tipo].some(
        a => a.alerta_id === alerta.alerta_id
      );

      if (!existe) {
        this.itemsList[tipo].push(alerta);
        this.snackbar.success("Há um novo alerta pendente")
      }
    }
  }

  /**
   * Notifica sobre novos alertas críticos
   */
  notifyNewCriticalAlert(count: number): void {
    const message = `⚠️ ${count} novo(s) alerta(s) crítico(s) detectado(s)!`;

    // Notificação visual
    this.snackbar.error(message);

    // Anúncio para leitores de tela
    this.announceToScreenReader(message, 'assertive');

    // Som de alerta
    if (this.audioContext) {
      this.playAlertSound();
    }

    // Notificação do navegador
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Alerta Crítico de Glicemia', {
        body: `${count} novo(s) alerta(s) crítico(s) detectado(s)`,
        icon: '/assets/icons/alert-icon.png',
        badge: '/assets/icons/badge-icon.png',
        tag: 'critical-alert',
        requireInteraction: true
      });
    }
  }

  /**
   * Toca som de alerta
   */
  playAlertSound(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.2);
  }

  /**
   * Busca lista de alertas
   */
  getListItems(): void {
    this.loading = true;
    let filter: any = '';

    if(this.status_id === 1 || this.status_id === 2) {
      filter += `&finalizados=${this.status_id === 2}`;
    }
    if(this.municipioId != null){
      filter += `&municipio_id=${this.municipioId}`;
    }
    if(this.unidadeS_id != null){
      filter += `&unidade_id=${this.unidadeS_id}`;
    }

    this.pendingPatientsAlertService.getPendingAlerts(filter).subscribe({
      next: (response: any) => {
        this.treatResponse(response);
      },
      error: (err: PagedContactTypeResponse) => {
        this.announceToScreenReader('Erro ao carregar alertas');
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  /**
   * Anuncia mensagem para leitores de tela
   */
  private announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}
